import { RouterInput } from "@mindlercare/mindlerapi-client";
import { Spacing, Spinner } from "@mindlercare/ui-web";
import { Box } from "@mui/material";
import CONSTANTS from "core/constants";
import { useCurrentCountryId } from "core/selectors/marketSelectors";
import { useSelectedPsychologistIdParam } from "hooks/useSelectedPsychologistParam";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { Typography } from "components/typography";
import { useUserHasRole } from "../../hooks/userHooks";
import { Table } from "../Table";
import { PsychologistDropdown } from "../lib/PsychologistDropdown";
import { PatientsFiltering } from "./PatientsFiltering";
import { PatientsSearchForm } from "./PatientsSearchForm";
import {
  type TreatmentStatusTabCounts,
  type TreatmentStatusTabValue,
} from "./TreatmentStatusTabs";
import { Container } from "./styled";
import { BookingStatus, BookingStatusCounts } from "./types";
import { usePatients } from "./usePatients";

export type SearchTerms =
  RouterInput["patients"]["getAllPatients"]["searchTerms"];

const TREATMENT_START = CONSTANTS.TREATMENT_EVENT_TYPE.TREATMENT_START;
const TREATMENT_END = CONSTANTS.TREATMENT_EVENT_TYPE.TREATMENT_END;

const Patients = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const isAdmin = useUserHasRole("Admin");
  const countryId = useCurrentCountryId();
  const [adminSelectedPsychologistUserId, setSelectedPsychologistId] =
    useSelectedPsychologistIdParam();
  const [searchTerms, setSearchTerms] = useState<SearchTerms>({});
  const [isSelectedPsychologistReset, setIsSelectedPsychologistReset] =
    useState(false);
  const [treatmentStatusFilter, setTreatmentStatusFilter] =
    useState<TreatmentStatusTabValue>(TREATMENT_START);

  const selectedPsychologistId =
    isAdmin && adminSelectedPsychologistUserId
      ? adminSelectedPsychologistUserId
      : -1;

  const showBookingStatusFilter =
    !isAdmin || (isAdmin && selectedPsychologistId !== -1);

  const defaultBookingStatus: BookingStatus = undefined;

  const [selectedBookingStatus, setSelectedBookingStatus] =
    useState<BookingStatus>(defaultBookingStatus);

  const { data, columns, isPending, columnsVisibility } = usePatients({
    searchTerms,
    selectedPsychologistId,
    shouldFetch: !isAdmin || isSelectedPsychologistReset,
  });

  const bookingStatus = showBookingStatusFilter
    ? selectedBookingStatus
    : defaultBookingStatus;

  const treatmentStatusCounts = useMemo<TreatmentStatusTabCounts>(() => {
    const counts: TreatmentStatusTabCounts = {
      ALL: 0,
      [TREATMENT_START]: 0,
      [TREATMENT_END]: 0,
    };

    for (const patient of data) {
      const status = patient?.treatmentStatusType as TreatmentStatusTabValue;
      const resolvedStatus =
        status === TREATMENT_END ? TREATMENT_END : TREATMENT_START;

      counts[resolvedStatus] += 1;
      counts.ALL += 1;
    }

    return counts;
  }, [data]);

  const { filteredByTreatmentStatus, bookingStatusCounts } = useMemo(() => {
    const filtered = treatmentStatusFilter
      ? data.filter((p) => {
          const status = p.treatmentStatusType as TreatmentStatusTabValue;
          if (treatmentStatusFilter === TREATMENT_START) {
            return status == null || status === TREATMENT_START;
          }

          return status === treatmentStatusFilter;
        })
      : data;

    const counts: BookingStatusCounts = {
      ALL: 0,
      ongoing: 0,
      unscheduled: 0,
      new: 0,
      upcoming_with_colleague: 0,
    };

    filtered.forEach(({ patientBookingStatus }) => {
      counts.ALL += 1;
      if (patientBookingStatus) counts[patientBookingStatus] += 1;
    });

    return {
      filteredByTreatmentStatus: filtered,
      bookingStatusCounts: counts,
    };
  }, [data, treatmentStatusFilter]);

  const filteredPatients = useMemo(() => {
    if (!bookingStatus) return filteredByTreatmentStatus;

    return filteredByTreatmentStatus.filter(
      (patient) => patient.patientBookingStatus === bookingStatus,
    );
  }, [bookingStatus, filteredByTreatmentStatus]);

  // Note: This effect is unfortunately a necessity due to our
  // system limitation of forcing Admins to be psychologists.
  // meaning that all search of patients will be limited to selected psychologists,
  // which for CS means they won't find any and would need to deselect their user in the
  // psychologists dropdown.
  useEffect(() => {
    if (!isAdmin) return;

    setSelectedPsychologistId(null);

    // Avoid multiple mutations since the admin is removed as selected psychologist
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setIsSelectedPsychologistReset(true);
  }, [isAdmin, setSelectedPsychologistId]);

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setSearchTerms({});

    setTreatmentStatusFilter(TREATMENT_START);

    setSelectedBookingStatus(defaultBookingStatus);
  }, [countryId, defaultBookingStatus, selectedPsychologistId]);

  const handleClick = (userId: number) => {
    navigate(
      `/patients/psychologist/${selectedPsychologistId}/patient/${userId}/${CONSTANTS.PATIENT_CARD_TABS.BOOKINGS.toLowerCase()}`,
    );
  };

  return (
    <Container>
      <div>
        <Typography insensitive variant="h1">
          {t("PATIENTS")}
        </Typography>
        {isAdmin && (
          <div>
            <Box sx={{ mt: "24px" }} />
            <Typography insensitive variant="body-x-small">
              {t("PSYCHOLOGISTS")}
            </Typography>
            <Box sx={{ mt: "6px" }} />
            <PsychologistDropdown />
          </div>
        )}
        <Box sx={{ mt: "24px" }} />

        <PatientsSearchForm onSearch={setSearchTerms} />

        <PatientsFiltering
          treatmentStatusFilter={treatmentStatusFilter}
          treatmentStatusCounts={treatmentStatusCounts}
          onTreatmentStatusChange={setTreatmentStatusFilter}
          showBookingStatusFilter={showBookingStatusFilter}
          bookingStatus={bookingStatus}
          onBookingStatusChange={setSelectedBookingStatus}
          bookingStatusCounts={bookingStatusCounts}
        />

        {isPending ? (
          <Spinner />
        ) : (
          <>
            <Spacing t={24} />
            <Table
              fixed
              data={filteredPatients}
              columns={columns}
              onRowClick={(e) => handleClick(e.userId)}
              columnVisibility={columnsVisibility}
            />
          </>
        )}
      </div>
    </Container>
  );
};

export default Patients;
